/**
 * Printora Gang Builder — core measurement, pricing, DPI, geometry.
 * All print dimensions are stored in millimetres (float).
 */
(() => {
  'use strict';

  const MM_PER_IN = 25.4;
  const PRODUCTION_DPI = 300;

  const DEFAULT_CONFIG = {
    sheetWidthMm: 300,
    safeMarginMm: 0,
    defaultSpacingMm: 3,
    minSpacingMm: 1,
    maxSpacingMm: 20,
    defaultSheetLengthMm: 1000,
    minSheetLengthMm: 200,
    maxSheetLengthMm: 10000,
    billingIncrementMm: 100,
    minBillableLengthMm: 1000,
    dpiGreen: 300,
    dpiAmber: 200,
    tier1Rate: 13,
    tier2ThresholdM: 3,
    tier2Rate: 12,
    tier3ThresholdM: 10,
    tier3Rate: 10,
    defaultDpi: 300,
  };

  function roundMm(v) {
    return Math.round(Number(v) * 1000) / 1000;
  }

  function mmToIn(mm) {
    return mm / MM_PER_IN;
  }

  function inToMm(inches) {
    return inches * MM_PER_IN;
  }

  function mmToPx(mm, dpi = PRODUCTION_DPI) {
    return (Number(mm) / MM_PER_IN) * dpi;
  }

  function pxToMm(px, dpi = PRODUCTION_DPI) {
    return (Number(px) / dpi) * MM_PER_IN;
  }

  /** Display scale: mm → screen pixels at current zoom */
  function mmToDisplayPx(mm, pxPerMm) {
    return Number(mm) * pxPerMm;
  }

  function displayPxToMm(px, pxPerMm) {
    return Number(px) / pxPerMm;
  }

  function effectiveDpi(pixelWidth, widthMm) {
    if (!widthMm || !pixelWidth) return 0;
    return (Number(pixelWidth) / widthMm) * MM_PER_IN;
  }

  function dpiQuality(dpi, config = DEFAULT_CONFIG) {
    if (dpi >= config.dpiGreen) return { level: 'green', label: 'Excellent print quality' };
    if (dpi >= config.dpiAmber) return { level: 'amber', label: 'May lose some sharpness' };
    return { level: 'red', label: 'Low resolution' };
  }

  function priceGangSheet(lengthMm, config = DEFAULT_CONFIG) {
    const metres = Math.max(0, Number(lengthMm) / 1000);
    let rate = config.tier1Rate;
    if (metres >= config.tier3ThresholdM) rate = config.tier3Rate;
    else if (metres >= config.tier2ThresholdM) rate = config.tier2Rate;

    const billableMm = Math.max(
      config.minBillableLengthMm,
      Math.ceil(Number(lengthMm) / config.billingIncrementMm) * config.billingIncrementMm
    );
    const billableMetres = billableMm / 1000;
    const price = Math.round(billableMetres * rate * 100) / 100;

    return {
      price,
      rate,
      billableMm,
      billableMetres,
      artworkMm: Number(lengthMm),
      metres,
    };
  }

  function cartQuantityForLength(lengthMm, config = DEFAULT_CONFIG) {
    const billableMm = Math.max(
      config.minBillableLengthMm,
      Math.ceil(Number(lengthMm) / config.billingIncrementMm) * config.billingIncrementMm
    );
    return Math.max(1, Math.ceil(billableMm / 1000));
  }

  function trimTransparentCanvas(image) {
    const canvas = document.createElement('canvas');
    canvas.width = image.naturalWidth || image.width;
    canvas.height = image.naturalHeight || image.height;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(image, 0, 0);
    const { data, width, height } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    let top = height;
    let left = width;
    let right = 0;
    let bottom = 0;
    let found = false;
    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const alpha = data[(y * width + x) * 4 + 3];
        if (alpha > 8) {
          found = true;
          if (x < left) left = x;
          if (x > right) right = x;
          if (y < top) top = y;
          if (y > bottom) bottom = y;
        }
      }
    }
    if (!found) return { canvas, trimmed: false, offsetX: 0, offsetY: 0 };
    const w = right - left + 1;
    const h = bottom - top + 1;
    const out = document.createElement('canvas');
    out.width = w;
    out.height = h;
    out.getContext('2d').drawImage(canvas, left, top, w, h, 0, 0, w, h);
    return { canvas: out, trimmed: true, offsetX: left, offsetY: top, width: w, height: h };
  }

  function canvasToImage(canvas) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = canvas.toDataURL('image/png');
    });
  }

  function aabbFromItem(item) {
    const rad = ((item.rotationDeg || 0) * Math.PI) / 180;
    const w = item.widthMm;
    const h = item.heightMm;
    const cos = Math.abs(Math.cos(rad));
    const sin = Math.abs(Math.sin(rad));
    const bw = w * cos + h * sin;
    const bh = w * sin + h * cos;
    return {
      x: item.xMm - (bw - w) / 2,
      y: item.yMm - (bh - h) / 2,
      width: bw,
      height: bh,
    };
  }

  function boxesOverlap(a, b, gapMm) {
    const g = Math.max(0, Number(gapMm) || 0);
    const tol = 0.25;
    return !(
      a.x + a.width + g <= b.x + tol
      || b.x + b.width + g <= a.x + tol
      || a.y + a.height + g <= b.y + tol
      || b.y + b.height + g <= a.y + tol
    );
  }

  function itemInBounds(item, sheetWidthMm, sheetHeightMm, marginMm) {
    const box = aabbFromItem(item);
    return (
      box.x >= marginMm - 0.01
      && box.y >= marginMm - 0.01
      && box.x + box.width <= sheetWidthMm - marginMm + 0.01
      && box.y + box.height <= sheetHeightMm - marginMm + 0.01
    );
  }

  function hasCollisions(items, sheetWidthMm, sheetHeightMm, marginMm, gapMm, ignoreIds = new Set()) {
    for (let i = 0; i < items.length; i += 1) {
      const a = items[i];
      if (ignoreIds.has(a.instanceId)) continue;
      if (!itemInBounds(a, sheetWidthMm, sheetHeightMm, marginMm)) return true;
      const boxA = aabbFromItem(a);
      for (let j = i + 1; j < items.length; j += 1) {
        const b = items[j];
        if (ignoreIds.has(b.instanceId)) continue;
        if (boxesOverlap(boxA, aabbFromItem(b), gapMm)) return true;
      }
    }
    return false;
  }

  function usedLengthMm(items, minLengthMm = 0) {
    if (!items.length) return minLengthMm;
    const maxY = Math.max(...items.map((item) => aabbFromItem(item).y + aabbFromItem(item).height));
    return roundMm(Math.max(minLengthMm, maxY));
  }

  function utilisation(items, sheetWidthMm, lengthMm) {
    if (!items.length || !lengthMm) return 0;
    const used = items.reduce((sum, item) => sum + item.widthMm * item.heightMm, 0);
    const total = sheetWidthMm * lengthMm;
    return total > 0 ? Math.round((used / total) * 100) : 0;
  }

  function uid(prefix = 'i') {
    return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  }

  function pricingUpsell(lengthMm, config = DEFAULT_CONFIG) {
    const current = priceGangSheet(lengthMm, config);
    const metres = current.billableMetres;
    const messages = [];

    if (metres < config.tier2ThresholdM) {
      const needMm = config.tier2ThresholdM * 1000 - current.billableMm;
      const needM = (needMm / 1000).toFixed(2);
      const atTier2 = priceGangSheet(config.tier2ThresholdM * 1000, config);
      const save = Math.round((current.rate - config.tier2Rate) * metres * 100) / 100;
      messages.push({
        type: 'tier',
        text: `Add ${needM} m more to unlock £${config.tier2Rate}/m pricing`,
        highlight: save > 0 ? `Save up to £${save.toFixed(2)}/m on longer sheets` : null,
      });
    } else if (metres < config.tier3ThresholdM) {
      const needMm = config.tier3ThresholdM * 1000 - current.billableMm;
      const needM = (needMm / 1000).toFixed(2);
      messages.push({
        type: 'tier',
        text: `Add ${needM} m more to unlock £${config.tier3Rate}/m pricing`,
        highlight: `Best value for bulk orders`,
      });
    } else {
      messages.push({
        type: 'success',
        text: `You're on our best rate — £${config.tier3Rate}/m`,
        highlight: null,
      });
    }

    return { current, messages, tiers: [
      { label: 'Under 3 m', rate: config.tier1Rate, active: metres < config.tier2ThresholdM },
      { label: '3 – 10 m', rate: config.tier2Rate, active: metres >= config.tier2ThresholdM && metres < config.tier3ThresholdM },
      { label: '10 m+', rate: config.tier3Rate, active: metres >= config.tier3ThresholdM },
    ] };
  }

  function removeWhiteBackground(canvas, tolerance = 240) {
    return removeBackgroundAdvanced(canvas, { whiteTol: tolerance, blackTol: 0, feather: 0 });
  }

  /**
   * Remove the background of any colour.
   *
   * 1. Sample the border of the photo and cluster it into up to four reference background
   *    colours (handles white, grey, coloured backdrops and simple gradients/vignettes).
   * 2. Flood-fill from the edges: a pixel joins the background if it is close to ANY reference
   *    colour (perceptual "redmean" distance). Comparing to the references — not to the
   *    neighbouring pixel — stops the fill from creeping through soft shadows into the design.
   * 3. Pixels just outside the fill that are nearly background get partial alpha (soft edge),
   *    and the mask is lightly eroded so no fringe of the old background remains.
   */
  function removeAnyBackground(canvas, opts = {}) {
    const tol = Math.max(6, Number(opts.tolerance ?? 48));
    const feather = Math.max(0, Number(opts.feather ?? 1));
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    const fullW = canvas.width;
    const fullH = canvas.height;
    if (!fullW || !fullH) return canvas;

    const maxSide = 1100;
    const scale = Math.min(1, maxSide / Math.max(fullW, fullH));
    const w = Math.max(1, Math.round(fullW * scale));
    const h = Math.max(1, Math.round(fullH * scale));
    const work = document.createElement('canvas');
    work.width = w;
    work.height = h;
    const wctx = work.getContext('2d', { willReadFrequently: true });
    wctx.drawImage(canvas, 0, 0, w, h);
    const { data } = wctx.getImageData(0, 0, w, h);
    const n = w * h;

    /* ---- 1. reference colours from the border ring ---- */
    const ring = Math.max(1, Math.round(Math.min(w, h) * 0.015));
    const buckets = new Map();
    const addSample = (i) => {
      const o = i * 4;
      if (data[o + 3] < 8) return;
      const key = ((data[o] >> 4) << 8) | ((data[o + 1] >> 4) << 4) | (data[o + 2] >> 4);
      const b = buckets.get(key) || { c: 0, r: 0, g: 0, b: 0 };
      b.c += 1; b.r += data[o]; b.g += data[o + 1]; b.b += data[o + 2];
      buckets.set(key, b);
    };
    let sampled = 0;
    for (let y = 0; y < h; y += 1) {
      const edgeRow = y < ring || y >= h - ring;
      for (let x = 0; x < w; x += 1) {
        if (edgeRow || x < ring || x >= w - ring) { addSample(y * w + x); sampled += 1; }
      }
    }
    const refs = [...buckets.values()]
      .sort((a, b) => b.c - a.c)
      .filter((b, idx) => idx === 0 || b.c >= sampled * 0.06)
      .slice(0, 4)
      .map((b) => [b.r / b.c, b.g / b.c, b.b / b.c]);
    if (!refs.length) refs.push([255, 255, 255]);

    /* Perceptual distance (redmean approximation of CIE76), scaled so tol behaves like RGB units */
    const distToRefs = (o) => {
      const r = data[o]; const g = data[o + 1]; const b = data[o + 2];
      let best = Infinity;
      for (let k = 0; k < refs.length; k += 1) {
        const rr = (r + refs[k][0]) / 2;
        const dr = r - refs[k][0]; const dg = g - refs[k][1]; const db = b - refs[k][2];
        const d = Math.sqrt((2 + rr / 256) * dr * dr + 4 * dg * dg + (2 + (255 - rr) / 256) * db * db) / 1.732;
        if (d < best) best = d;
      }
      return best;
    };

    /* ---- 2. flood fill from the edges ---- */
    const dist = new Float32Array(n);
    for (let i = 0; i < n; i += 1) dist[i] = data[i * 4 + 3] < 8 ? 0 : distToRefs(i * 4);
    const isBg = new Uint8Array(n);
    const queue = new Int32Array(n);
    let qh = 0;
    let qt = 0;
    const seed = (i) => { if (!isBg[i] && dist[i] <= tol) { isBg[i] = 1; queue[qt++] = i; } };
    for (let x = 0; x < w; x += 1) { seed(x); seed((h - 1) * w + x); }
    for (let y = 0; y < h; y += 1) { seed(y * w); seed(y * w + w - 1); }
    while (qh < qt) {
      const i = queue[qh++];
      const x = i % w;
      if (x > 0) seed(i - 1);
      if (x < w - 1) seed(i + 1);
      if (i >= w) seed(i - w);
      if (i + w < n) seed(i + w);
    }

    /* ---- 3. alpha mask: hard core, soft rim, slight erosion of the subject edge ---- */
    const alpha = new Uint8ClampedArray(n);
    const softTol = tol * 1.75;
    for (let i = 0; i < n; i += 1) {
      if (isBg[i]) { alpha[i] = 0; continue; }
      const x = i % w;
      const nearBg = (x > 0 && isBg[i - 1]) || (x < w - 1 && isBg[i + 1]) || (i >= w && isBg[i - w]) || (i + w < n && isBg[i + w]);
      if (nearBg && dist[i] < softTol) {
        alpha[i] = Math.round(255 * Math.min(1, (dist[i] - tol) / (softTol - tol)));
      } else {
        alpha[i] = 255;
      }
    }
    if (feather > 0) {
      /* one-pixel erosion + blur of the rim so no halo of background colour survives */
      const eroded = new Uint8ClampedArray(alpha);
      for (let i = 0; i < n; i += 1) {
        if (!alpha[i]) continue;
        const x = i % w;
        let m = alpha[i];
        if (x > 0) m = Math.min(m, alpha[i - 1]);
        if (x < w - 1) m = Math.min(m, alpha[i + 1]);
        if (i >= w) m = Math.min(m, alpha[i - w]);
        if (i + w < n) m = Math.min(m, alpha[i + w]);
        eroded[i] = Math.round((alpha[i] + m) / 2);
      }
      alpha.set(eroded);
    }

    const mask = document.createElement('canvas');
    mask.width = w;
    mask.height = h;
    const mctx = mask.getContext('2d');
    const maskData = mctx.createImageData(w, h);
    for (let i = 0; i < n; i += 1) {
      const o = i * 4;
      maskData.data[o] = 255;
      maskData.data[o + 1] = 255;
      maskData.data[o + 2] = 255;
      maskData.data[o + 3] = alpha[i];
    }
    mctx.putImageData(maskData, 0, 0);

    const fullMask = document.createElement('canvas');
    fullMask.width = fullW;
    fullMask.height = fullH;
    const fm = fullMask.getContext('2d');
    fm.imageSmoothingEnabled = true;
    fm.imageSmoothingQuality = 'high';
    fm.drawImage(mask, 0, 0, fullW, fullH);

    ctx.save();
    ctx.globalCompositeOperation = 'destination-in';
    ctx.drawImage(fullMask, 0, 0);
    ctx.restore();
    return canvas;
  }

  /**
   * Auto background removal: tries increasing sensitivities until the border of the photo is
   * (almost) fully transparent, so noisy/jpeg backgrounds are removed without the user tuning.
   */
  function autoRemoveBackground(canvas, opts = {}) {
    const attempts = opts.tolerances || [34, 48, 64, 82];
    const w = canvas.width;
    const h = canvas.height;
    if (!w || !h) return canvas;
    const original = cloneCanvas(canvas);
    const borderCoverage = (c) => {
      const cctx = c.getContext('2d', { willReadFrequently: true });
      const ring = Math.max(1, Math.round(Math.min(w, h) * 0.01));
      let opaque = 0;
      let total = 0;
      const count = (img) => {
        for (let i = 3; i < img.data.length; i += 4) { total += 1; if (img.data[i] > 40) opaque += 1; }
      };
      count(cctx.getImageData(0, 0, w, ring));
      count(cctx.getImageData(0, h - ring, w, ring));
      count(cctx.getImageData(0, 0, ring, h));
      count(cctx.getImageData(w - ring, 0, ring, h));
      return total ? opaque / total : 0;
    };
    let best = null;
    let bestScore = Infinity;
    for (let k = 0; k < attempts.length; k += 1) {
      const trial = cloneCanvas(original);
      removeAnyBackground(trial, { tolerance: attempts[k], feather: opts.feather ?? 1 });
      const score = borderCoverage(trial);
      if (score < bestScore) { best = trial; bestScore = score; }
      if (score <= 0.02) break;
    }
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(best || original, 0, 0);
    return canvas;
  }

  function removeBackgroundAdvanced(canvas, opts = {}) {
    const mode = opts.mode || 'any';
    if (mode === 'any' || mode === 'auto') {
      return removeAnyBackground(canvas, {
        tolerance: opts.tolerance ?? 48,
        feather: opts.feather ?? 1,
      });
    }
    const whiteTol = Number(opts.whiteTol ?? 235);
    const blackTol = Number(opts.blackTol ?? 0);
    const feather = Number(opts.feather ?? 0);
    const ctx = canvas.getContext('2d');
    const { data, width, height } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      let alpha = data[i + 3];
      if (whiteTol > 0) {
        const wMin = Math.min(r, g, b);
        if (wMin >= whiteTol) alpha = 0;
        else if (feather > 0 && wMin > whiteTol - feather) {
          alpha = Math.round(alpha * (1 - (wMin - (whiteTol - feather)) / feather));
        }
      }
      if (blackTol > 0 && alpha > 0) {
        const bMax = Math.max(r, g, b);
        if (bMax <= blackTol) alpha = 0;
        else if (feather > 0 && bMax < blackTol + feather) {
          alpha = Math.round(alpha * ((bMax - blackTol) / feather));
        }
      }
      data[i + 3] = alpha;
    }
    ctx.putImageData(new ImageData(data, width, height), 0, 0);
    return canvas;
  }

  function removeColorFromCanvas(canvas, opts = {}) {
    const tr = Number(opts.r ?? 255);
    const tg = Number(opts.g ?? 255);
    const tb = Number(opts.b ?? 255);
    const tol = Number(opts.tolerance ?? 32);
    const feather = Number(opts.feather ?? 8);
    const ctx = canvas.getContext('2d');
    const { data, width, height } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    for (let i = 0; i < data.length; i += 4) {
      const dist = Math.sqrt(
        (data[i] - tr) ** 2 + (data[i + 1] - tg) ** 2 + (data[i + 2] - tb) ** 2
      );
      if (dist <= tol) data[i + 3] = 0;
      else if (feather > 0 && dist < tol + feather) {
        data[i + 3] = Math.round(data[i + 3] * ((dist - tol) / feather));
      }
    }
    ctx.putImageData(new ImageData(data, width, height), 0, 0);
    return canvas;
  }

  function applyEraserDot(ctx, x, y, size, hardness) {
    const r = Math.max(1, size / 2);
    const hard = Math.max(0, Math.min(1, hardness / 100));
    const grad = ctx.createRadialGradient(x, y, r * hard * 0.15, x, y, r);
    grad.addColorStop(0, 'rgba(0,0,0,1)');
    grad.addColorStop(hard, 'rgba(0,0,0,1)');
    grad.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.save();
    ctx.globalCompositeOperation = 'destination-out';
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  function applyEraserStroke(canvas, x0, y0, x1, y1, size, hardness) {
    const ctx = canvas.getContext('2d');
    const dist = Math.hypot(x1 - x0, y1 - y0);
    const steps = Math.max(1, Math.ceil(dist / (size * 0.25)));
    for (let i = 0; i <= steps; i += 1) {
      const t = i / steps;
      applyEraserDot(ctx, x0 + (x1 - x0) * t, y0 + (y1 - y0) * t, size, hardness);
    }
    return canvas;
  }

  function cloneCanvas(canvas) {
    const c = document.createElement('canvas');
    c.width = canvas.width;
    c.height = canvas.height;
    c.getContext('2d').drawImage(canvas, 0, 0);
    return c;
  }

  function applyColorAdjust(canvas, { hue = 0, saturation = 100, brightness = 100 } = {}) {
    const out = document.createElement('canvas');
    out.width = canvas.width;
    out.height = canvas.height;
    const ctx = out.getContext('2d');
    ctx.filter = `hue-rotate(${hue}deg) saturate(${saturation}%) brightness(${brightness}%)`;
    ctx.drawImage(canvas, 0, 0);
    return out;
  }

  function parseConfigFromRoot(root) {
    const d = root?.dataset || {};
    return {
      ...DEFAULT_CONFIG,
      sheetWidthMm: Number(d.gsbSheetWidth || DEFAULT_CONFIG.sheetWidthMm),
      safeMarginMm: Number(d.gsbSafeMargin || DEFAULT_CONFIG.safeMarginMm),
      defaultSpacingMm: Number(d.gsbGap || DEFAULT_CONFIG.defaultSpacingMm),
      defaultSheetLengthMm: Number(d.gsbDefaultLength || DEFAULT_CONFIG.defaultSheetLengthMm),
      tier1Rate: Number(d.gsbTier1 || DEFAULT_CONFIG.tier1Rate),
      tier2Rate: Number(d.gsbTier2 || DEFAULT_CONFIG.tier2Rate),
      tier3Rate: Number(d.gsbTier3 || DEFAULT_CONFIG.tier3Rate),
      tier2ThresholdM: Number(d.gsbTier2Threshold || DEFAULT_CONFIG.tier2ThresholdM),
      tier3ThresholdM: Number(d.gsbTier3Threshold || DEFAULT_CONFIG.tier3ThresholdM),
      variantId: d.gsbVariantId || '',
      defaultDpi: Number(d.gsbDefaultDpi || DEFAULT_CONFIG.defaultDpi),
    };
  }

  window.PrintoraGangBuilder = {
    MM_PER_IN,
    PRODUCTION_DPI,
    DEFAULT_CONFIG,
    roundMm,
    mmToIn,
    inToMm,
    mmToPx,
    pxToMm,
    mmToDisplayPx,
    displayPxToMm,
    effectiveDpi,
    dpiQuality,
    priceGangSheet,
    cartQuantityForLength,
    trimTransparentCanvas,
    canvasToImage,
    aabbFromItem,
    boxesOverlap,
    itemInBounds,
    hasCollisions,
    usedLengthMm,
    utilisation,
    uid,
    pricingUpsell,
    removeWhiteBackground,
    removeAnyBackground,
    autoRemoveBackground,
    removeBackgroundAdvanced,
    removeColorFromCanvas,
    applyEraserStroke,
    applyEraserDot,
    cloneCanvas,
    applyColorAdjust,
    parseConfigFromRoot,
  };

  // Legacy alias
  window.PrintoraGangSheet = window.PrintoraGangSheet || {
    DEFAULTS: DEFAULT_CONFIG,
    mmFromPixels: (px, dpi) => pxToMm(px, dpi),
    roundMm,
    nestItems: (...args) => window.PrintoraGangBuilderNest?.nestItems(...args),
    priceGangSheet: (len, opts) => priceGangSheet(len, { ...DEFAULT_CONFIG, ...opts }),
    cartQuantityForLength: (len, opts) => cartQuantityForLength(len, { ...DEFAULT_CONFIG, ...opts }),
  };
})();
